import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { useLang } from "../context/LanguageContext";

const NavLink = ({ href, children, onClick, testid, light }) => (
  <a
    href={href}
    onClick={onClick}
    data-testid={testid}
    className={`relative text-[0.78rem] tracking-[0.22em] uppercase font-medium transition-colors duration-300 py-2 after:absolute after:left-0 after:bottom-0 after:h-px after:w-full after:origin-left after:scale-x-0 hover:after:scale-x-100 after:transition-transform after:duration-500 ${
      light
        ? "text-ivory/85 hover:text-ivory after:bg-ivory"
        : "text-charcoal/80 hover:text-charcoal after:bg-charcoal"
    }`}
  >
    {children}
  </a>
);

const Navbar = () => {
  const { lang, setLang, t } = useLang();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const close = () => setOpen(false);
  const light = !scrolled;

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-ivory/85 backdrop-blur-xl border-b border-charcoal/10"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="mx-auto max-w-[1400px] px-6 sm:px-10 flex items-center justify-between h-20">
        <Link
          to="/"
          data-testid="brand-link"
          className="flex items-center gap-3 group"
          onClick={close}
        >
          <span
            className={`font-heading text-2xl sm:text-3xl tracking-tight transition-colors ${
              light ? "text-ivory" : "text-charcoal"
            }`}
          >
            Tenjara
          </span>
          <span
            className={`hidden sm:inline-block h-4 w-px transition-colors ${
              light ? "bg-ivory/60" : "bg-charcoal/25"
            }`}
          />
          <span
            className={`hidden sm:inline-block text-[0.7rem] tracking-[0.25em] uppercase font-medium transition-colors ${
              light ? "text-gold" : "text-olive"
            }`}
          >
            Lombok
          </span>
        </Link>

        <nav className="hidden md:flex items-center gap-10">
          <NavLink href="/#about" testid="nav-about" light={light}>{t("nav.about")}</NavLink>
          <NavLink href="/#projects" testid="nav-projects" light={light}>{t("nav.projects")}</NavLink>
          <NavLink href="/#guide" testid="nav-guide" light={light}>{t("nav.guide")}</NavLink>
          <NavLink href="/#contact" testid="nav-contact" light={light}>{t("nav.contact")}</NavLink>
        </nav>

        <div className="flex items-center gap-4">
          <div
            className="hidden sm:flex items-center text-[0.72rem] tracking-[0.2em] uppercase font-medium"
            data-testid="lang-toggle"
          >
            <button
              onClick={() => setLang("en")}
              data-testid="lang-en"
              className={`px-2 py-1 transition-colors ${
                lang === "en"
                  ? light ? "text-ivory" : "text-charcoal"
                  : light ? "text-ivory/50 hover:text-ivory/80" : "text-charcoal/35 hover:text-charcoal/70"
              }`}
            >
              EN
            </button>
            <span className={light ? "text-ivory/30" : "text-charcoal/20"}>/</span>
            <button
              onClick={() => setLang("es")}
              data-testid="lang-es"
              className={`px-2 py-1 transition-colors ${
                lang === "es"
                  ? light ? "text-ivory" : "text-charcoal"
                  : light ? "text-ivory/50 hover:text-ivory/80" : "text-charcoal/35 hover:text-charcoal/70"
              }`}
            >
              ES
            </button>
          </div>

          <button
            className={`md:hidden p-2 -mr-2 ${light ? "text-ivory" : "text-charcoal"}`}
            onClick={() => setOpen((v) => !v)}
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          className="md:hidden bg-ivory/95 backdrop-blur-xl border-t border-charcoal/10"
          data-testid="mobile-menu"
        >
          <div className="px-6 py-8 flex flex-col gap-6">
            <NavLink href="/#about" onClick={close} testid="mobile-nav-about">
              {t("nav.about")}
            </NavLink>
            <NavLink href="/#projects" onClick={close} testid="mobile-nav-projects">
              {t("nav.projects")}
            </NavLink>
            <NavLink href="/#guide" onClick={close} testid="mobile-nav-guide">
              {t("nav.guide")}
            </NavLink>
            <NavLink href="/#contact" onClick={close} testid="mobile-nav-contact">
              {t("nav.contact")}
            </NavLink>
            <div className="flex items-center gap-2 pt-4 border-t border-charcoal/10">
              <button
                onClick={() => setLang("en")}
                className={`text-xs tracking-[0.2em] uppercase ${lang === "en" ? "text-charcoal" : "text-charcoal/40"}`}
              >
                EN
              </button>
              <span className="text-charcoal/20">/</span>
              <button
                onClick={() => setLang("es")}
                className={`text-xs tracking-[0.2em] uppercase ${lang === "es" ? "text-charcoal" : "text-charcoal/40"}`}
              >
                ES
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
